import { ClickAwayListener } from '@mui/material'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useVideoLanguage } from '~/hooks/useGetVideoLanguage'
import { useLang } from '../../useLang'
import { useLogger } from '../../useLogger'
import { useVideoScale } from '../../useVideoScale'
import { PlayerButton } from '../PlayerButton'
import { MenuItem } from './MenuItem'

type Props = {
  vtts: {
    default: boolean
    url: string
    locale: string
    label: string
  }[]
}

export const Subtitle = ({ vtts }: Props) => {
  const { videoScale } = useVideoScale()
  const { lang, setLang } = useLang()
  const [showSubtitleMenu, setShowSubtitleMenu] = useState(false)
  const { t } = useTranslation()
  const { clickSubtitleButton, changeSubtitle } = useLogger()

  const { getVideoLanguage, updateVideoLanguage } = useVideoLanguage()

  useEffect(() => {
    const videoLanguage = getVideoLanguage(vtts)
    if (videoLanguage === 'disabled') {
      setLang(null)
    } else {
      setLang(videoLanguage)
    }
  }, [vtts, getVideoLanguage, setLang])

  const handleChangeLanguage = useCallback(
    (locale: string | null) => {
      setLang(locale)

      if (locale === null) {
        updateVideoLanguage('disabled')
      } else {
        updateVideoLanguage(locale)
      }

      changeSubtitle(locale || 'disabled')
    },
    [setLang, updateVideoLanguage, changeSubtitle],
  )

  return (
    <ClickAwayListener onClickAway={() => setShowSubtitleMenu(false)}>
      <div style={{ position: 'relative' }}>
        <PlayerButton
          onClick={() => {
            setShowSubtitleMenu(!showSubtitleMenu)
            clickSubtitleButton()
          }}
        >
          <Image
            src='/player/subtitle.png'
            alt='Subtitle'
            width={videoScale * 52}
            height={videoScale * 52}
          />
          {videoScale > 0.4 && (
            <span
              style={{
                fontSize: `${videoScale * 24}px`,
                fontWeight: 'bold',
                marginLeft: '8px',
              }}
            >
              {t('Subtitles')}
            </span>
          )}
        </PlayerButton>
        {showSubtitleMenu && (
          <div
            style={{
              minWidth: '120px',
              position: 'absolute',
              bottom: `${videoScale * 104}px`,
              left: 0,
              backgroundColor: '#EBECED',
              color: 'black',
              borderRadius: '8px',
              padding: '8px 0',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <MenuItem
              onClick={() => handleChangeLanguage(null)}
              active={lang === null}
              text={t('Off')}
            />
            {vtts.map((vtt) => (
              <MenuItem
                key={vtt.url}
                onClick={() => handleChangeLanguage(vtt.locale)}
                active={lang === vtt.locale}
                text={vtt.label}
              />
            ))}
          </div>
        )}
      </div>
    </ClickAwayListener>
  )
}
